// DISP-5a — pure helpers for the dispatch (BL) web path. No React, no formatting: the panels format with utils/format.
// Caps mirror the API (DISP-3): free/dispatch cap = qty_remaining - qty_open_dispatched; a slip confirm is capped at the dispatched qty.
import { serverMessage } from '../../lib/idempotencyKey.mjs';

const num = v => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const r3 = x => Math.round(x * 1000) / 1000;
const DEAD = ['cancelled', 'quote', 'delivered'];

// What a new slip (or a free delivery) may still take on this order line.
export const lineCap = l => Math.max(0, r3(num(l.qty_remaining) - num(l.qty_open_dispatched)));
export const isLive = l => !DEAD.includes(l.delivery_status);
export const dispatchableLines = lines => (lines || []).filter(l => isLive(l) && lineCap(l) > 0);

// Order-level gate for the Record Dispatch button (D17): only a posted order with something left to dispatch.
export const canDispatch = (header, lines) => (header?.tx_status === 'posted') && !['quote', 'cancelled', 'delivered'].includes(header?.delivery_status) && dispatchableLines(lines).length > 0;

// Why Record Dispatch is not offered on an order (null = dispatchable): 'notPosted' | 'closed' (quote / cancelled) | 'direct' (delivered at posting, no slip) |
// 'delivered' | 'covered' (every remaining unit already on open slips). Mirrors canDispatch; the UI shows a disabled button with this reason.
export function dispatchBlockReason(header, lines, slips = []) {
  if (!header || !header.tx_status) return 'loading';   // the order payload (or its header) is not in yet: never claim 'not posted' from an empty object
  if (header.tx_status !== 'posted') return 'notPosted';
  if (['quote', 'cancelled'].includes(header?.delivery_status) || header?.order_lifecycle === 'cancelled') return 'closed';
  if (header?.delivery_status === 'delivered') return (slips || []).length ? 'delivered' : 'direct';
  return dispatchableLines(lines).length ? null : 'covered';
}

// "Available at source" (mock A2): physical on hand at the line's stock node minus what open slips already hold there (all orders).
// null when the API did not supply the figures. Mirrors the server's D9 guard, which stays the authority.
export const availableAtSource = l => (l.stock_physical_at_node == null || l.node_open_dispatched == null ? null : r3(num(l.stock_physical_at_node) - num(l.node_open_dispatched)));
// 'positive' | 'overRemain' | 'overNode' | null for one line's typed quantity (the mock's two inline errors: OVER_DISPATCH_LINE / OVER_DISPATCH_NODE).
export function lineIssue(l, raw) {
  const k = qtyProblem(raw, lineCap(l));
  if (k === 'exceeds') return 'overRemain';
  if (k) return 'positive';
  const av = availableAtSource(l);
  return av != null && r3(Number(raw)) > av ? 'overNode' : null;
}

export const slipsOf = (dispatches, status) => (dispatches || []).filter(d => d.status === status);

// 'positive' | 'exceeds' | null for one typed quantity against a cap (dispatch create/edit, free delivery, slip confirm).
export function qtyProblem(raw, cap) {
  if (raw == null || raw === '') return 'positive';
  const q = Number(raw);
  if (!Number.isFinite(q) || q <= 0) return 'positive';
  return r3(q) > r3(num(cap)) ? 'exceeds' : null;
}

// Form state for DispatchPanel: every dispatchable line ticked at its cap; when editing a draft, only its lines are ticked at their slip quantity.
export function initialPick(lines, slip) {
  const own = {};
  (slip?.lines || []).forEach(sl => { own[sl.sales_id] = sl.quantity; });
  const pick = {};
  dispatchableLines(lines).forEach(l => {
    const mine = own[l.sale_id];
    pick[l.sale_id] = slip ? { on: mine != null, qty: String(mine != null ? mine : lineCap(l)) } : { on: true, qty: String(lineCap(l)) };
  });
  return pick;
}

// -> { lines, problem } ; problem = { sale_id, kind } for the first bad line, or { kind: 'none' } when nothing is ticked.
export function buildDispatchLines(lines, pick) {
  const out = [];
  for (const l of dispatchableLines(lines)) {
    const p = pick[l.sale_id];
    if (!p || !p.on) continue;
    const kind = qtyProblem(p.qty, lineCap(l));
    if (kind) return { lines: [], problem: { sale_id: l.sale_id, kind } };
    out.push({ sales_id: l.sale_id, quantity: Number(p.qty) });
  }
  return out.length ? { lines: out, problem: null } : { lines: [], problem: { kind: 'none' } };
}

// Slip confirm (D4/D14): EVERY slip line is sent, each > 0 and <= dispatched; lower = shortfall back to commitment.
export function initialSlipQtys(slip) {
  const q = {};
  (slip?.lines || []).forEach(sl => { q[sl.id] = String(sl.quantity); });
  return q;
}
export function buildSlipDelivery(slip, qtys) {
  const lines = [];
  for (const sl of slip.lines || []) {
    const kind = qtyProblem(qtys[sl.id], sl.quantity);
    if (kind) return { lines: [], problem: { id: sl.id, kind } };
    lines.push({ sale_id: sl.sales_id, quantity: Number(qtys[sl.id]), dispatch_id: sl.id });
  }
  return { lines, problem: null };
}
export function slipShortfall(slip, qtys) {
  return r3((slip.lines || []).reduce((s, sl) => s + Math.max(0, num(sl.quantity) - (Number.isFinite(Number(qtys[sl.id])) ? Number(qtys[sl.id]) : 0)), 0));
}

// "BL-2026-00012" or the draft placeholder.
export const slipLabel = (slip, draftLabel) => slip?.dispatch_no || draftLabel;
// Which open slip holds a line (for the "fully reserved by BL-x" note).
export const reservingSlips = (dispatches, saleId) => slipsOf(dispatches, 'open').filter(d => (d.lines || []).some(l => l.sales_id === saleId)).map(d => d.dispatch_no);

// API refusal -> one line in the UI language (API EN/FR message) + its code, or a caller fallback.
export function refusalOf(resp, lang, fallback) {
  const data = resp?.data || {};
  return { code: data.code || null, text: serverMessage(data, lang) || fallback, dispatches: data.dispatches || null };
}
export const isOpenDispatchRefusal = data => data?.code === 'OPEN_DISPATCH_EXISTS';

// ---- Cancel & reissue (owner design): a NEW draft form pre-filled from a cancelled slip; nothing is created until the user saves/issues it ----
// pick: only the old slip's lines that are dispatchable again (the cancel released their reservation), at min(old quantity, what the line can take now)
export function reissuePick(lines, oldSlip) {
  const own = {};
  (oldSlip?.lines || []).forEach(sl => { own[sl.sales_id] = num(sl.quantity); });
  const pick = {};
  dispatchableLines(lines).forEach(l => { const q = own[l.sale_id]; pick[l.sale_id] = q == null ? { on: false, qty: String(lineCap(l)) } : { on: true, qty: String(Math.min(q, lineCap(l))) }; });
  return pick;
}
// header: same vehicle / driver / deliver-to; dispatch date = today (the old date may be past), dispatch ETA starts as that date (the user changes it: that is the point);
// the notes start with 'Reissue of BL-…' (the only trace on the new slip, no schema change)
export const reissueHeader = (oldSlip, today, label = 'Reissue of') => ({
  dispatch_date: today, eta: today, delivery_method: oldSlip?.delivery_method === 'pickup' ? 'pickup' : 'delivery', pickup_phone: oldSlip?.pickup_phone || '', pickup_id_note: oldSlip?.pickup_id_note || '',
  vehicle: oldSlip?.vehicle || '', driver: oldSlip?.driver || '', deliver_to: oldSlip?.deliver_to || '',
  notes: `${label} ${oldSlip?.dispatch_no || '—'}${oldSlip?.notes ? ` — ${oldSlip.notes}` : ''}`,
});
// ── GES-431 DIRECT-BL: the optional delivered BL of a direct-delivery order (db/763). Pure helpers. ──
export const directBlOf = dispatches => (dispatches || []).find(d => d.kind === 'direct') || null;
// Order page: "Issue BL" only on a posted, delivered, direct order that has none; "Print BL" when it has one.
export const canIssueDirectBl = (header, dispatches) =>
  header?.order_type === 'direct' && header?.tx_status === 'posted' && header?.delivery_status === 'delivered' && !directBlOf(dispatches);
// New Sale form: the choice is offered only for a posted direct sale being created (not a quote, a planned/future-dated sale, a deferred order, or a quote conversion).
export const offerIssueBl = ({ deliveredNow, isQuote, planned, editingQuote }) => !!deliveredNow && !isQuote && !planned && !editingQuote;
// The request field: present ONLY when the user ticked the box (and it is offered): a sale without it sends the byte-identical body as before.
export const issueBlField = (checked, offered) => (checked && offered ? { issue_bl: true } : {});
// POST /api/sales `bl` -> what the form says. null when the sale carried no `bl` (the box was not ticked).
export function blOutcomeView(bl) {
  if (!bl) return null;
  if (bl.issued) return { kind: 'issued', no: bl.dispatch_no, slipId: bl.slip_id, replay: !!bl.replay };
  return { kind: 'failed', code: bl.code || null, en: bl.error || '', fr: bl.error_fr || '' };
}
